import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Tooltip } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ApiService } from '../../../services/api';
import { Store } from '../../../types';
import { CropImage } from '../../components/common/CropImage';
import { ForgeApiError, ForgeApiService } from '../../services/ForgeApiService';
import {
  LiveStatusChangedCropRow,
  LiveStatusChangeMetric,
  LiveStatusComputationDetail,
  LiveStatusFieldDiff,
  LiveStatusSnapshot,
  LiveStatusWarningDetail,
  LiveStatusWarningSeverity,
  LiveStatusWarningSummaryItem,
} from '../../types';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../../utils/safeStorage';

const CHAIN_STORAGE_KEY = 'forge_live_status_chain_id';
const SUPPORTED_CHAIN_IDS = new Set<number>([46]);
const DEFAULT_ESTIMATED_DURATION_MS = 30000;
const SUPPORTED_WARNING_KEYS = new Set(['active_5mp_crops', 'crop_non_bool_is_active']);

type ChainOption = {
  chainId: number;
  chainName: string;
  timeZone: string;
};

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatRelativeAge(ageSeconds: number): string {
  if (ageSeconds < 60) return 'computed just now';
  if (ageSeconds < 3600) return `computed ${Math.floor(ageSeconds / 60)}m ago`;
  if (ageSeconds < 86400) {
    const hours = Math.floor(ageSeconds / 3600);
    const minutes = Math.floor((ageSeconds % 3600) / 60);
    return `computed ${hours}h ${String(minutes).padStart(2, '0')}m ago`;
  }
  const days = Math.floor(ageSeconds / 86400);
  const hours = Math.floor((ageSeconds % 86400) / 3600);
  return hours > 0 ? `computed ${days}d ${hours}h ago` : `computed ${days}d ago`;
}

function formatTimestampUtc(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value));
}

function formatTimestampInTimeZone(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(new Date(value));
}

function formatCurrentTimeInTimeZone(value: Date, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone,
  }).format(value);
}

function formatDurationFromSeconds(totalSeconds: number): string {
  if (totalSeconds < 60) return `${Math.max(totalSeconds, 1)}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function formatChangedCropStatus(row: LiveStatusChangedCropRow): string {
  if (row.isActiveType === 'bool') {
    return row.isActiveValue === 'true' ? 'active' : 'inactive';
  }
  if (!row.isActiveValue) {
    return '—';
  }
  return row.isActiveType ? `${row.isActiveValue} (${row.isActiveType})` : row.isActiveValue;
}

function formatCropMeta(row: LiveStatusChangedCropRow): string | null {
  const tokens = [row.orientation, row.classificationType, row.imageResolution].filter(Boolean);
  return tokens.length > 0 ? tokens.join(' · ') : null;
}

function formatDiffLabel(field: string): string {
  switch (field) {
    case 'forge_origin_source':
      return 'Forge origin source';
    case 'normalized_barcode':
      return 'Normalized barcode';
    case 'design_id':
      return 'Design';
    case 'classification_type':
      return 'Classification';
    case 'is_active':
      return 'is_active';
    case 'is_pending':
      return 'is_pending';
    case 'pending_reason':
      return 'Pending reason';
    default:
      return field.replaceAll('_', ' ');
  }
}

function formatDiffValue(value?: string | null): string {
  return value && value.length > 0 ? value : '—';
}

function getFreshnessTone(ageSeconds: number): {
  color: string;
  background: string;
  border: string;
} {
  if (ageSeconds < 4 * 3600) {
    return {
      color: 'var(--positive)',
      background: 'var(--positive-subtle)',
      border: 'var(--accent-border)',
    };
  }
  if (ageSeconds <= 24 * 3600) {
    return {
      color: 'var(--warning)',
      background: 'var(--warning-subtle)',
      border: 'rgba(210,153,34,0.28)',
    };
  }
  return {
    color: 'var(--danger)',
    background: 'var(--danger-subtle)',
    border: 'rgba(248,81,73,0.28)',
  };
}

function loadStoredChainId(): number | null {
  const stored = safeLocalStorageGetItem(CHAIN_STORAGE_KEY);
  if (!stored) return null;
  const parsed = Number(stored);
  return Number.isFinite(parsed) ? parsed : null;
}

function saveStoredChainId(chainId: number): void {
  safeLocalStorageSetItem(CHAIN_STORAGE_KEY, String(chainId));
}

function durationStorageKey(chainId: number): string {
  return `forge_live_status_estimated_duration_ms:${chainId}`;
}

function loadChainDurationMs(chainId: number): number | null {
  const stored = safeLocalStorageGetItem(durationStorageKey(chainId));
  if (!stored) return null;
  const parsed = Number(stored);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function saveChainDurationMs(chainId: number, value: number): void {
  safeLocalStorageSetItem(durationStorageKey(chainId), String(Math.round(value)));
}

function estimateDurationMs(lastDurationMs: number | null | undefined): number {
  if (!lastDurationMs || lastDurationMs <= 0) {
    return DEFAULT_ESTIMATED_DURATION_MS;
  }
  return Math.round(lastDurationMs * 1.1);
}

function severityTone(severity: LiveStatusWarningSeverity): {
  color: string;
  background: string;
  border: string;
} {
  if (severity === 'good') {
    return {
      color: 'var(--positive)',
      background: 'var(--positive-subtle)',
      border: 'var(--accent-border)',
    };
  }
  return severity === 'critical'
    ? {
        color: 'var(--danger)',
        background: 'var(--danger-subtle)',
        border: 'rgba(248,81,73,0.28)',
      }
    : {
        color: 'var(--warning)',
        background: 'var(--warning-subtle)',
        border: 'rgba(210,153,34,0.28)',
      };
}

const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)' };

const controlStyle: React.CSSProperties = {
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundColor: 'var(--surface)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '0 34px 0 12px',
  height: 32,
  fontSize: 13,
  cursor: 'pointer',
  outline: 'none',
  minWidth: 180,
};

const actionButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  borderRadius: 6,
  border: '1px solid var(--accent-border)',
  background: 'var(--accent-subtle)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  padding: '0 14px',
  height: 32,
  fontSize: 13,
  fontWeight: 600,
};

const metricCardStyle: React.CSSProperties = {
  padding: 18,
  minHeight: 126,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background:
    'linear-gradient(180deg, color-mix(in srgb, var(--surface) 88%, var(--accent-subtle) 12%), var(--surface))',
};

const tooltipSlotProps = {
  tooltip: {
    sx: {
      backgroundColor: 'var(--surface)',
      backgroundImage: 'none',
      opacity: 1,
      color: 'var(--text-primary)',
      border: '1px solid color-mix(in srgb, var(--border) 82%, var(--accent-border) 18%)',
      boxShadow: '0 18px 42px rgba(0,0,0,0.16), 0 6px 18px rgba(0,0,0,0.10)',
      borderRadius: '12px',
      fontFamily: 'var(--font-body)',
      fontSize: 12.5,
      fontWeight: 500,
      letterSpacing: '-0.01em',
      maxWidth: 260,
      padding: '11px 13px',
    },
  },
  popper: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  },
};

const tooltipTextStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  lineHeight: 1.38,
  fontFamily: 'var(--font-body)',
  letterSpacing: '-0.01em',
};

const detailListStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'grid',
  gap: 6,
};

function renderComputationDetailContent(
  detail?: LiveStatusComputationDetail | null
): React.ReactNode {
  if (!detail) {
    return <div>Computation metadata unavailable for this cached snapshot.</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontWeight: 700 }}>{detail.label}</div>
      <ul style={detailListStyle}>
        <li>
          <strong>DB:</strong> {detail.databases.join(', ')}
        </li>
        <li>
          <strong>Collection / table:</strong> {detail.collectionsOrTables.join(', ')}
        </li>
        <li>
          <strong>Time:</strong> {detail.timeBasis}
        </li>
        <li>
          <strong>Filter:</strong> {detail.filters.join(' | ') || 'None'}
        </li>
        {detail.joins.length > 0 ? (
          <li>
            <strong>Join:</strong> {detail.joins.join(' | ')}
          </li>
        ) : null}
        <li>
          <strong>Count:</strong> {detail.countDefinition}
        </li>
        <li>
          <strong>Severity:</strong> {detail.severityRule}
        </li>
      </ul>
    </div>
  );
}

const infoTriggerStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '1px solid color-mix(in srgb, var(--border) 84%, var(--accent-border) 16%)',
  background: 'color-mix(in srgb, var(--surface) 84%, var(--accent-subtle) 16%)',
  color: 'var(--text-tertiary)',
  cursor: 'help',
  flexShrink: 0,
};

const InsightPopover: React.FC<{
  content: React.ReactNode;
  children: React.ReactElement;
  placement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'left';
}> = ({ content, children, placement = 'top' }) => (
  <Tooltip
    title={<div style={tooltipTextStyle}>{content}</div>}
    placement={placement}
    enterDelay={90}
    leaveDelay={40}
    disableInteractive
    slotProps={tooltipSlotProps}
    TransitionProps={{ timeout: 160 }}
  >
    {children}
  </Tooltip>
);

const InfoHint: React.FC<{
  content: React.ReactNode;
  placement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'left';
}> = ({ content, placement }) => (
  <InsightPopover content={content} placement={placement ?? 'bottom-start'}>
    <span style={infoTriggerStyle}>
      <InfoOutlinedIcon style={{ fontSize: 12 }} />
    </span>
  </InsightPopover>
);

const ComputationDetailPopover: React.FC<{
  detail?: LiveStatusComputationDetail | null;
}> = ({ detail }) => {
  return <InfoHint content={renderComputationDetailContent(detail)} />;
};

const ProgressBanner: React.FC<{
  elapsedMs: number;
  estimatedDurationMs: number;
}> = ({ elapsedMs, estimatedDurationMs }) => {
  const elapsedSeconds = Math.max(1, Math.round(elapsedMs / 1000));
  const estimatedSeconds = Math.max(1, Math.round(estimatedDurationMs / 1000));
  const etaSeconds = Math.max(0, Math.round((estimatedDurationMs - elapsedMs) / 1000));
  const progressRatio =
    estimatedDurationMs > 0
      ? Math.min(elapsedMs / estimatedDurationMs, elapsedMs > estimatedDurationMs ? 0.96 : 0.92)
      : 0.2;
  const isOverEstimate = elapsedMs > estimatedDurationMs;

  return (
    <div
      style={{
        marginTop: 16,
        padding: '14px 16px',
        borderRadius: 12,
        border: '1px solid var(--accent-border)',
        background:
          'linear-gradient(135deg, color-mix(in srgb, var(--accent-subtle) 72%, var(--surface) 28%), color-mix(in srgb, var(--blue) 10%, var(--surface) 90%))',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--text-secondary)',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Refreshing Snapshot
          </div>
          <div style={{ marginTop: 6, fontSize: 14, fontWeight: 700 }}>
            Recomputing the current chain snapshot and replacing the cached latest version.
          </div>
          <div style={{ marginTop: 6, color: 'var(--text-secondary)', fontSize: 12 }}>
            Any existing snapshot stays visible while the new one is being written.
          </div>
        </div>
        <div style={{ display: 'grid', gap: 6, minWidth: 180 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
            <span style={{ color: 'var(--text-secondary)' }}>Elapsed</span>
            <span style={{ ...mono, fontWeight: 700 }}>
              {formatDurationFromSeconds(elapsedSeconds)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              {isOverEstimate ? 'Status' : 'Estimated left'}
            </span>
            <span style={{ ...mono, fontWeight: 700 }}>
              {isOverEstimate
                ? 'Running longer than estimate'
                : formatDurationFromSeconds(etaSeconds)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
            <span style={{ color: 'var(--text-secondary)' }}>Typical duration</span>
            <span style={{ ...mono, fontWeight: 700 }}>
              {formatDurationFromSeconds(estimatedSeconds)}
            </span>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 14,
          height: 10,
          background: 'color-mix(in srgb, var(--surface) 78%, var(--accent-subtle) 22%)',
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.max(progressRatio * 100, 8)}%`,
            height: '100%',
            borderRadius: 999,
            background: 'linear-gradient(90deg, var(--accent), var(--blue))',
            transition: 'width 250ms ease',
          }}
        />
      </div>
    </div>
  );
};

const MetricTile: React.FC<{
  label: string;
  value: string;
  detail: string;
  icon: React.ReactNode;
  infoContent?: React.ReactNode;
}> = ({ label, value, detail, icon, infoContent }) => (
  <div className="forge-card" style={metricCardStyle}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <div
        style={{
          color: 'var(--text-secondary)',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span>{label}</span>
        {infoContent ? <InfoHint content={infoContent} /> : null}
      </div>
      <div style={{ color: 'var(--accent)' }}>{icon}</div>
    </div>
    <div style={{ fontSize: 24, fontWeight: 800, marginTop: 12 }}>{value}</div>
    <div style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 12 }}>{detail}</div>
  </div>
);

const WarningSummaryCard: React.FC<{
  item: LiveStatusWarningSummaryItem;
  infoContent?: LiveStatusComputationDetail | null;
}> = ({ item, infoContent }) => {
  const tone = severityTone(item.severity);
  return (
    <div
      className="forge-card"
      style={{
        padding: 18,
        border: `1px solid ${tone.border}`,
        background:
          item.count > 0
            ? `linear-gradient(180deg, color-mix(in srgb, ${tone.background} 78%, var(--surface) 22%), var(--surface))`
            : 'var(--surface)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'var(--text-secondary)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>{item.label}</span>
            <ComputationDetailPopover detail={infoContent} />
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, marginTop: 10 }}>
            {formatNumber(item.count)}
          </div>
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            borderRadius: 999,
            padding: '5px 10px',
            border: `1px solid ${tone.border}`,
            background: tone.background,
            color: tone.color,
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          {item.severity === 'good' ? (
            <CheckCircleOutlineIcon style={{ fontSize: 14 }} />
          ) : (
            <WarningAmberOutlinedIcon style={{ fontSize: 14 }} />
          )}
          {item.severity}
        </div>
      </div>
    </div>
  );
};

const WarningDetailCard: React.FC<{
  detail: LiveStatusWarningDetail;
  chainTimeZone: string;
  infoContent?: LiveStatusComputationDetail | null;
}> = ({ detail, chainTimeZone, infoContent }) => {
  const tone = severityTone(detail.severity);

  return (
    <div className="forge-card" style={{ minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          alignItems: 'flex-start',
          marginBottom: 12,
        }}
      >
        <div>
          <div
            className="forge-card-title"
            style={{ marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <span>{detail.label}</span>
            <ComputationDetailPopover detail={infoContent} />
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 760 }}>
            {detail.description}
          </div>
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            borderRadius: 999,
            padding: '6px 12px',
            border: `1px solid ${tone.border}`,
            background: tone.background,
            color: tone.color,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <span>{formatNumber(detail.count)}</span>
          <span style={{ opacity: 0.7 }}>·</span>
          <span>{detail.severity}</span>
        </div>
      </div>
      {detail.examples.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Entity</th>
                <th>Linked crop</th>
                <th>Barcode</th>
                <th>Item #</th>
                <th>is_active</th>
                <th>Type</th>
                <th>Touched</th>
                <th>Store</th>
              </tr>
            </thead>
            <tbody>
              {detail.examples.map((example) => (
                <tr key={`${detail.key}-${example.entityId}`}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{example.entityType}</div>
                    <div style={{ ...mono, fontSize: 12, color: 'var(--text-secondary)' }}>
                      {example.entityId}
                    </div>
                  </td>
                  <td style={{ ...mono, color: 'var(--text-secondary)' }}>
                    {example.linkedCropId || '—'}
                  </td>
                  <td style={mono}>{example.normalizedBarcode || '—'}</td>
                  <td style={mono}>{example.itemNumber || '—'}</td>
                  <td style={mono}>{example.isActiveValue || '—'}</td>
                  <td style={mono}>{example.isActiveType || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {example.touchedAtUtc
                      ? formatTimestampInTimeZone(example.touchedAtUtc, chainTimeZone)
                      : '—'}
                  </td>
                  <td>
                    {example.storeName || (example.storeId ? `Store ${example.storeId}` : '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

const ChangedCropsCard: React.FC<{
  rows: LiveStatusChangedCropRow[];
  chainTimeZone: string;
  infoContent?: LiveStatusComputationDetail | null;
}> = ({ rows, chainTimeZone, infoContent }) => (
  <div className="forge-card" style={{ marginBottom: 18, minWidth: 0 }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        alignItems: 'flex-start',
        marginBottom: 12,
      }}
    >
      <div>
        <div
          className="forge-card-title"
          style={{ marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <span>Changed Crops</span>
          <ComputationDetailPopover detail={infoContent} />
        </div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Last hour, newest first.</div>
      </div>
    </div>
    {rows.length > 0 ? (
      <div style={{ display: 'grid', gap: 14 }}>
        {rows.map((row) => {
          const cropMeta = formatCropMeta(row);
          const diffRows = row.fieldDiffs ?? [];

          return (
            <div
              key={row.cropId}
              style={{
                display: 'grid',
                gridTemplateColumns: '120px minmax(0, 1fr)',
                gap: 16,
                padding: 16,
                border: '1px solid var(--border)',
                borderRadius: 14,
                background:
                  'linear-gradient(180deg, color-mix(in srgb, var(--surface) 90%, var(--accent-subtle) 10%), var(--surface))',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {row.imageUrl ? (
                  <CropImage imageUrl={row.imageUrl} size={104} borderRadius={10} />
                ) : (
                  <div
                    style={{
                      width: 104,
                      height: 104,
                      borderRadius: 10,
                      border: '1px solid var(--border)',
                      background:
                        'color-mix(in srgb, var(--surface) 88%, var(--accent-subtle) 12%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--text-tertiary)',
                      fontSize: 12,
                      textAlign: 'center',
                      padding: 8,
                    }}
                  >
                    No image
                  </div>
                )}
              </div>

              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 16,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {formatTimestampInTimeZone(row.touchedAtUtc, chainTimeZone)}
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          borderRadius: 999,
                          padding: '4px 10px',
                          border: '1px solid var(--accent-border)',
                          background: 'var(--accent-subtle)',
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                        }}
                      >
                        {row.changeKind}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {row.storeName || (row.storeId ? `Store ${row.storeId}` : '—')}
                      </div>
                    </div>

                    <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
                      <div style={{ ...mono, fontWeight: 700, fontSize: 13 }}>{row.cropId}</div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 12,
                          flexWrap: 'wrap',
                          color: 'var(--text-secondary)',
                          fontSize: 12,
                        }}
                      >
                        <span>
                          Barcode: <span style={mono}>{row.normalizedBarcode || '—'}</span>
                        </span>
                        <span>
                          Status: <span style={mono}>{formatChangedCropStatus(row)}</span>
                        </span>
                        {cropMeta ? <span>{cropMeta}</span> : null}
                      </div>
                    </div>
                  </div>

                  {row.masterLabelUrl ? (
                    <Link
                      to={row.masterLabelUrl}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        borderRadius: 8,
                        border: '1px solid var(--accent-border)',
                        background: 'var(--accent-subtle)',
                        color: 'var(--text-primary)',
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 700,
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span>Open in Master Label</span>
                      <OpenInNewIcon style={{ fontSize: 14 }} />
                    </Link>
                  ) : null}
                </div>

                {!row.baselineAvailable ? (
                  <div style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 12 }}>
                    Baseline unavailable. This snapshot does not have a previous crop-state baseline
                    to diff against yet.
                  </div>
                ) : diffRows.length > 0 ? (
                  <div style={{ marginTop: 14, overflowX: 'auto' }}>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Before</th>
                          <th>After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diffRows.map((diff: LiveStatusFieldDiff) => (
                          <tr key={`${row.cropId}-${diff.field}`}>
                            <td>{formatDiffLabel(diff.field)}</td>
                            <td style={mono}>{formatDiffValue(diff.before)}</td>
                            <td style={mono}>{formatDiffValue(diff.after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 12 }}>
                    No tracked field changes captured for this crop in the current baseline diff.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div
        style={{
          borderRadius: 12,
          border: '1px solid var(--accent-border)',
          background:
            'linear-gradient(180deg, color-mix(in srgb, var(--positive-subtle) 74%, var(--surface) 26%), var(--surface))',
          padding: '16px 18px',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'var(--positive)',
          }}
        >
          <CheckCircleOutlineIcon style={{ fontSize: 16 }} />
          No crop changes
        </div>
        <div style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
          No crop changes in the last hour.
        </div>
      </div>
    )}
  </div>
);

function changeDetail(metric: LiveStatusChangeMetric): string {
  return `${formatNumber(metric.createdCount)} created · ${formatNumber(metric.updatedCount)} updated · ${formatNumber(metric.isActiveFlips)} flips`;
}

export const LiveStatusDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  const [chainOptions, setChainOptions] = useState<ChainOption[]>([]);
  const [selectedChainId, setSelectedChainId] = useState<number | null>(loadStoredChainId);
  const [snapshot, setSnapshot] = useState<LiveStatusSnapshot | null>(null);
  const [isLoadingChains, setIsLoadingChains] = useState(true);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [progressStartedAt, setProgressStartedAt] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [estimatedDurationMs, setEstimatedDurationMs] = useState(DEFAULT_ESTIMATED_DURATION_MS);
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    let cancelled = false;

    const loadChains = async () => {
      setIsLoadingChains(true);
      try {
        const stores = await ApiService.fetchStores(token);
        if (cancelled) return;
        const chainsById = stores.reduce<Map<number, ChainOption>>((acc, store: Store) => {
          if (!acc.has(store.chainId)) {
            acc.set(store.chainId, {
              chainId: store.chainId,
              chainName: store.chainLegibleName || store.chainName,
              timeZone: store.timeZone,
            });
          }
          return acc;
        }, new Map());
        const nextOptions = [...chainsById.values()]
          .filter((option) => SUPPORTED_CHAIN_IDS.has(option.chainId))
          .sort((a, b) => a.chainName.localeCompare(b.chainName));
        setChainOptions(nextOptions);

        const storedChainId = loadStoredChainId();
        const resolvedChainId =
          nextOptions.find((option) => option.chainId === storedChainId)?.chainId ??
          nextOptions[0]?.chainId ??
          null;
        setSelectedChainId(resolvedChainId);
        if (resolvedChainId !== null) {
          saveStoredChainId(resolvedChainId);
        }
      } catch (error) {
        if (!cancelled) {
          showErrorToast(error instanceof Error ? error.message : 'Error loading chains');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingChains(false);
        }
      }
    };

    loadChains();
    return () => {
      cancelled = true;
    };
  }, [showErrorToast, token]);

  useEffect(() => {
    if (!selectedChainId) {
      setSnapshot(null);
      setEstimatedDurationMs(DEFAULT_ESTIMATED_DURATION_MS);
      return;
    }
    setEstimatedDurationMs(estimateDurationMs(loadChainDurationMs(selectedChainId)));
  }, [selectedChainId]);

  useEffect(() => {
    if (!selectedChainId) {
      setSnapshot(null);
      return;
    }

    let cancelled = false;

    const loadSnapshot = async () => {
      setIsLoadingSnapshot(true);
      try {
        const nextSnapshot = await ForgeApiService.fetchLiveStatus(token, selectedChainId);
        if (cancelled) return;
        setSnapshot(nextSnapshot);
        const storedDurationMs = loadChainDurationMs(selectedChainId);
        const preferredDurationMs = Math.max(
          nextSnapshot.snapshot.computeDurationMs ?? 0,
          storedDurationMs ?? 0
        );
        if (preferredDurationMs > 0) {
          saveChainDurationMs(selectedChainId, preferredDurationMs);
        }
        setEstimatedDurationMs(estimateDurationMs(preferredDurationMs || null));
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ForgeApiError && error.status === 404) {
          setSnapshot(null);
          setEstimatedDurationMs(estimateDurationMs(loadChainDurationMs(selectedChainId)));
        } else {
          showErrorToast(error instanceof Error ? error.message : 'Error loading live status');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSnapshot(false);
        }
      }
    };

    loadSnapshot();
    return () => {
      cancelled = true;
    };
  }, [selectedChainId, showErrorToast, token]);

  useEffect(() => {
    if (progressStartedAt === null) return;
    const updateElapsed = () => {
      setElapsedMs(Date.now() - progressStartedAt);
    };
    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 250);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [progressStartedAt]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const handleChainChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextChainId = Number(event.target.value);
    setSelectedChainId(nextChainId);
    saveStoredChainId(nextChainId);
  };

  const handleRefresh = async () => {
    if (!selectedChainId) return;
    const startedAt = Date.now();
    setIsRefreshing(true);
    setProgressStartedAt(startedAt);
    setElapsedMs(0);
    try {
      const nextSnapshot = await ForgeApiService.refreshLiveStatus(token, selectedChainId);
      setSnapshot(nextSnapshot);
      const actualDurationMs = Date.now() - startedAt;
      const completedDurationMs = Math.max(
        nextSnapshot.snapshot.computeDurationMs ?? 0,
        actualDurationMs
      );
      saveChainDurationMs(selectedChainId, completedDurationMs);
      setEstimatedDurationMs(estimateDurationMs(completedDurationMs));
      showSuccessToast('Live status snapshot refreshed');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error refreshing live status');
    } finally {
      setIsRefreshing(false);
      setProgressStartedAt(null);
      setElapsedMs(0);
    }
  };

  const selectedChain = chainOptions.find((option) => option.chainId === selectedChainId) ?? null;
  const snapshotMatchesSelection = snapshot !== null && snapshot.chain.chainId === selectedChainId;
  const selectedChainTimeZone = snapshotMatchesSelection
    ? snapshot.chain.timeZone
    : (selectedChain?.timeZone ?? 'UTC');
  const currentChainTimeLabel = formatCurrentTimeInTimeZone(currentTime, selectedChainTimeZone);
  const liveAgeSeconds =
    snapshotMatchesSelection && snapshot
      ? Math.max(
          0,
          Math.floor(
            (currentTime.getTime() - new Date(snapshot.snapshot.computedAtUtc).getTime()) / 1000
          )
        )
      : 0;

  const warningTotal = useMemo(
    () =>
      snapshot?.warningsSummary
        .filter((item) => SUPPORTED_WARNING_KEYS.has(item.key))
        .reduce((sum, item) => sum + item.count, 0) ?? 0,
    [snapshot]
  );
  const activeWarningCategories = useMemo(
    () =>
      snapshot?.warningsSummary
        .filter((item) => SUPPORTED_WARNING_KEYS.has(item.key))
        .filter((item) => item.count > 0).length ?? 0,
    [snapshot]
  );
  const supportedWarningsSummary = useMemo(
    () => snapshot?.warningsSummary.filter((item) => SUPPORTED_WARNING_KEYS.has(item.key)) ?? [],
    [snapshot]
  );
  const supportedWarningDetails = useMemo(
    () => snapshot?.warningDetails.filter((detail) => SUPPORTED_WARNING_KEYS.has(detail.key)) ?? [],
    [snapshot]
  );
  const visibleWarningDetails = useMemo(
    () => supportedWarningDetails.filter((detail) => detail.count > 0),
    [supportedWarningDetails]
  );
  const changedCrops = snapshot?.changedCrops ?? [];
  const changedCropsInfoDetail =
    snapshot?.computationDetails?.changesLastHour?.changedCrops ??
    snapshot?.computationDetails?.changesLastHour?.['changed_crops'] ??
    null;

  return (
    <div className="page-content page-content--wide">
      <div className="forge-card" style={{ marginBottom: 20, padding: 20 }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Live Status Snapshot
            </div>
            <div style={{ marginTop: 8, fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>
              {selectedChain?.chainName || 'Select a chain'}
            </div>
            <div style={{ marginTop: 10, color: 'var(--text-secondary)', maxWidth: 720 }}>
              Current-state chain snapshot for last-hour changes across crops and designs, with fast
              crop-side integrity warnings.
            </div>
            <div
              style={{
                marginTop: 14,
                display: 'grid',
                gap: 4,
                color: 'var(--text-secondary)',
                fontSize: 12,
              }}
            >
              <div>
                Chain time zone:{' '}
                <span style={{ color: 'var(--text-primary)' }}>{selectedChainTimeZone}</span>
              </div>
              <div>
                Current chain time:{' '}
                <span style={{ color: 'var(--text-primary)' }}>{currentChainTimeLabel}</span>
              </div>
              {snapshotMatchesSelection && snapshot ? (
                <div>
                  Last-hour window:{' '}
                  <span style={{ color: 'var(--text-primary)' }}>
                    {formatTimestampInTimeZone(
                      snapshot.window.lastHourStartedAtUtc,
                      selectedChainTimeZone
                    )}
                  </span>{' '}
                  to{' '}
                  <span style={{ color: 'var(--text-primary)' }}>
                    {formatTimestampInTimeZone(
                      snapshot.window.lastHourEndedAtUtc,
                      selectedChainTimeZone
                    )}
                  </span>
                </div>
              ) : (
                <div>
                  Refresh computes the current chain state and overwrites the cached latest
                  snapshot.
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            {snapshotMatchesSelection && snapshot ? (
              <>
                <div
                  style={(() => {
                    const tone = getFreshnessTone(liveAgeSeconds);
                    return {
                      display: 'inline-flex',
                      alignItems: 'center',
                      alignSelf: 'flex-end',
                      borderRadius: 10,
                      padding: '4px 9px',
                      border: `1px solid ${tone.border}`,
                      background: tone.background,
                      color: tone.color,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                    } as React.CSSProperties;
                  })()}
                >
                  {formatRelativeAge(liveAgeSeconds)}
                </div>
                <div style={{ textAlign: 'right', color: 'var(--text-secondary)', fontSize: 12 }}>
                  <div>Computed {formatTimestampUtc(snapshot.snapshot.computedAtUtc)} UTC</div>
                  <div>
                    Chain time{' '}
                    {formatTimestampInTimeZone(
                      snapshot.snapshot.computedAtUtc,
                      selectedChainTimeZone
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginTop: 20,
          }}
        >
          <div style={{ position: 'relative' }}>
            <select
              value={selectedChainId ?? ''}
              onChange={handleChainChange}
              disabled={isLoadingChains || chainOptions.length === 0}
              style={controlStyle}
            >
              {chainOptions.length === 0 ? (
                <option value="">
                  {isLoadingChains ? 'Loading chains…' : 'No supported chains'}
                </option>
              ) : null}
              {chainOptions.map((option) => (
                <option key={option.chainId} value={option.chainId}>
                  {option.chainName}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={!selectedChainId || isRefreshing}
            style={{
              ...actionButtonStyle,
              opacity: !selectedChainId || isRefreshing ? 0.65 : 1,
              cursor: !selectedChainId || isRefreshing ? 'not-allowed' : 'pointer',
            }}
          >
            <RefreshOutlinedIcon style={{ fontSize: 16 }} />
            {isRefreshing ? 'Refreshing…' : 'Refresh Snapshot'}
          </button>
        </div>

        {isRefreshing ? (
          <ProgressBanner elapsedMs={elapsedMs} estimatedDurationMs={estimatedDurationMs} />
        ) : null}
      </div>

      {!snapshotMatchesSelection && !isLoadingSnapshot ? (
        <div className="forge-card" style={{ marginBottom: 20 }}>
          <div className="forge-card-title">No Snapshot</div>
          <div style={{ color: 'var(--text-secondary)', maxWidth: 680 }}>
            No cached live-status snapshot exists yet for this chain. Refresh once to compute the
            current status and store the latest snapshot.
          </div>
        </div>
      ) : null}

      {snapshotMatchesSelection && snapshot ? (
        <>
          <div
            className="stats-row"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              marginBottom: 18,
            }}
          >
            <MetricTile
              label="Crops Touched"
              value={formatNumber(snapshot.changesLastHour.crops.touchedCount)}
              detail={changeDetail(snapshot.changesLastHour.crops)}
              icon={<Inventory2OutlinedIcon style={{ fontSize: 20 }} />}
              infoContent={renderComputationDetailContent(
                snapshot.computationDetails?.changesLastHour?.crops
              )}
            />
            <MetricTile
              label="Designs Touched"
              value={formatNumber(snapshot.changesLastHour.designs.touchedCount)}
              detail={`${formatNumber(snapshot.changesLastHour.designs.createdCount)} created · ${formatNumber(snapshot.changesLastHour.designs.updatedCount)} updated`}
              icon={<AutoAwesomeMosaicOutlinedIcon style={{ fontSize: 20 }} />}
              infoContent={renderComputationDetailContent(
                snapshot.computationDetails?.changesLastHour?.designs
              )}
            />
            <MetricTile
              label="Current Warnings"
              value={formatNumber(warningTotal)}
              detail={`${formatNumber(activeWarningCategories)} active categories`}
              icon={<WarningAmberOutlinedIcon style={{ fontSize: 20 }} />}
              infoContent={
                <div>
                  Sum of current warning-category counts after applying the live-status severity
                  rules.
                </div>
              }
            />
          </div>

          <ChangedCropsCard
            rows={changedCrops}
            chainTimeZone={selectedChainTimeZone}
            infoContent={changedCropsInfoDetail}
          />

          <div className="forge-card" style={{ marginBottom: 18 }}>
            <div className="forge-card-title" style={{ marginBottom: 14 }}>
              Warning Summary
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 16,
              }}
            >
              {supportedWarningsSummary.map((item) => (
                <WarningSummaryCard
                  key={item.key}
                  item={item}
                  infoContent={snapshot.computationDetails?.warnings?.[item.key] ?? null}
                />
              ))}
            </div>
          </div>

          {visibleWarningDetails.length > 0 ? (
            <div style={{ display: 'grid', gap: 18 }}>
              {visibleWarningDetails.map((detail) => (
                <WarningDetailCard
                  key={detail.key}
                  detail={detail}
                  chainTimeZone={selectedChainTimeZone}
                  infoContent={snapshot.computationDetails?.warnings?.[detail.key] ?? null}
                />
              ))}
            </div>
          ) : (
            <div
              className="forge-card"
              style={{
                border: '1px solid var(--accent-border)',
                background:
                  'linear-gradient(180deg, color-mix(in srgb, var(--positive-subtle) 74%, var(--surface) 26%), var(--surface))',
              }}
            >
              <div
                className="forge-card-title"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <CheckCircleOutlineIcon style={{ fontSize: 18, color: 'var(--positive)' }} />
                No Current Issues
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                No current issues detected for this chain.
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};
